import { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import AdminSidebar from "./AdminSidebar";
import {
  Box, AppBar, Toolbar, IconButton, Typography,
  useMediaQuery, useTheme, Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import { useApi } from "../hooks";
import { apiGetUser, apiLogutUser, method } from "../apiPath";
import keyNames from "../keyName";
import { getToast } from "../common/utils";
import ErrorBoundary from "../common/ErrorBoundary";
import { tataLogin, tataRefresh, getStoredTataToken, tataLogout } from "../services/tataAuth";

const toast = getToast("error");

const AdminLayout = () => {
  const hitApi = useApi();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isDetailPage =
    location.pathname === "/app/agent-management/database/details" ||
    location.pathname === "/app/contacts-engagement/detail";
  const userData = useSelector((state) => state.mainReducer.userData);
  const tataToken = useSelector((state) => state.mainReducer.tataToken);
  const { themeMode, isLoading } = useSelector((state) => state.mainReducer);
  const [open, setOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const refreshTimer = useRef(null);
  const mobileLogoutRef = useRef(false);
  const navAutoOpenedRef = useRef(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // On mobile: auto-open sidebar once when agent userData loads
  useEffect(() => {
    if (isMobile && userData?.role === "agent" && !navAutoOpenedRef.current) {
      navAutoOpenedRef.current = true;
      setMobileNavOpen(true);
    }
  }, [userData?.role, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  const dispatchTataToken = (token) => {
    dispatch({ type: keyNames.REUSABLE, keyName: keyNames.tataToken, payload: token });
  };

  // Mobile top-bar logout
  const handleMobileLogout = () => {
    mobileLogoutRef.current = true;
    hitApi(apiLogutUser, null, method.post, keyNames.userData);
  };

  useEffect(() => {
    if (mobileLogoutRef.current && isLoading === "noLoading") {
      mobileLogoutRef.current = false;
      if (tataToken) tataLogout(tataToken).catch(() => {});
      dispatch({ type: keyNames.REUSABLE, keyName: keyNames.tataToken, payload: null });
      sessionStorage.clear();
      dispatch({ type: keyNames.REUSABLE, keyName: keyNames.userData, payload: null });
      navigate("/");
    }
  }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Schedule Tata token refresh
  const scheduleRefresh = (currentToken, expiryMs) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    const delay = Math.max(expiryMs - Date.now() - 60_000, 5_000);
    refreshTimer.current = setTimeout(async () => {
      try {
        const { token, expiry } = await tataRefresh(currentToken);
        dispatchTataToken(token);
        scheduleRefresh(token, expiry);
      } catch (err) {
        console.warn("[Tata] refresh failed, re-login:", err.message);
        try {
          const { token, expiry } = await tataLogin();
          dispatchTataToken(token);
          scheduleRefresh(token, expiry);
        } catch {}
      }
    }, delay);
  };

  useEffect(() => {
    if (!userData) return;
    if (tataToken) return;
    const { tataSmartFlowId, tataSmartFlowPassword } = userData;
    if (!tataSmartFlowId || !tataSmartFlowPassword) return;
    const stored = getStoredTataToken();
    if (stored) {
      dispatchTataToken(stored.token);
      scheduleRefresh(stored.token, stored.expiry);
    } else {
      tataLogin(tataSmartFlowId, tataSmartFlowPassword)
        .then(({ token, expiry }) => {
          dispatchTataToken(token);
          scheduleRefresh(token, expiry);
        })
        .catch((err) => console.warn("[Tata] initial login failed:", err.message));
    }
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [userData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!userData) {
      hitApi(apiGetUser, null, method.get, keyNames.userData, toast);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // On mobile: no sidebar margin. On desktop: mini (56px) or full (200px).
  const sidebarWidth = isDetailPage ? 0 : isMobile ? 0 : open ? 200 : 56;

  return (
    userData && (
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          overflow: "hidden",
          bgcolor: themeMode === "dark" ? "#0f172a" : "#f1f5f9",
        }}
      >
        {/* Sidebar — hidden on mobile unless drawer is open */}
        {!isDetailPage && (
          <AdminSidebar
            open={isMobile ? mobileNavOpen : open}
            setOpen={isMobile ? setMobileNavOpen : setOpen}
            isMobile={isMobile}
            onMobileClose={() => setMobileNavOpen(false)}
          />
        )}

        {/* Main content area */}
        <Box
          sx={{
            ml: { xs: 0, md: `${sidebarWidth}px` },
            height: "100vh",
            width: { xs: "100vw", md: `calc(100vw - ${sidebarWidth}px)` },
            display: "flex",
            flexDirection: "column",
            overflow: "visible",
            transition: "margin-left 0.2s ease, width 0.2s ease",
          }}
        >
          {/* Mobile top AppBar — shown only on mobile, not on detail pages */}
          {isMobile && !isDetailPage && (
            <AppBar
              position="static"
              color="inherit"
              elevation={0}
              sx={{
                flexShrink: 0,
                bgcolor: "background.paper",
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Toolbar sx={{ minHeight: "52px !important", px: 1.5, gap: 0.5 }}>
                {/* Logo */}
                <img
                  src="/KestoneArch.png"
                  alt="Kestone"
                  style={{ height: 30, width: "auto" }}
                />
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  noWrap
                  sx={{ flexGrow: 1, ml: 0.5, fontSize: 14 }}
                >
                  Kestone CRM
                </Typography>

                {/* Profile button — opens nav drawer (profile at bottom) */}
                <Tooltip title={userData?.employeeName || "Profile"}>
                  <IconButton
                    size="small"
                    onClick={() => setMobileNavOpen(true)}
                    sx={{ color: "text.secondary" }}
                  >
                    <AccountCircleIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                </Tooltip>

                {/* Logout button */}
                <Tooltip title="Logout">
                  <IconButton size="small" color="error" onClick={handleMobileLogout}>
                    <LogoutIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>

                {/* Navigation menu */}
                <Tooltip title="Menu">
                  <IconButton
                    size="small"
                    edge="end"
                    onClick={() => setMobileNavOpen(true)}
                    sx={{ color: "text.primary" }}
                  >
                    <MenuIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                </Tooltip>
              </Toolbar>
            </AppBar>
          )}

          {/* Scrollable page area */}
          <Box
            sx={{
              flexGrow: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              p: { xs: 1.5, md: 2 },
            }}
          >
            <ErrorBoundary>
              <Box key={location.pathname} className="page-enter">
                <Outlet />
              </Box>
            </ErrorBoundary>
          </Box>
        </Box>
      </Box>
    )
  );
};

export default AdminLayout;
